"use client";

import { useEffect, useState } from "react";
import { Star, Loader2, CheckCircle2, ShieldCheck } from "lucide-react";
import StarRating from "@/components/StarRating";
import { LANDING_URL } from "@/lib/auth-shared";
import type { ProductReviewsPayload, PublicReview } from "@/lib/reviews";

type AuthState = "loading" | "guest" | "user";

export default function ProductReviews({
  productId,
  initial,
}: {
  productId: string;
  initial: ProductReviewsPayload;
}) {
  const [summary, setSummary] = useState(initial.summary);
  const [reviews, setReviews] = useState<PublicReview[]>(initial.reviews);

  const [auth, setAuth] = useState<AuthState>("loading");
  const [eligible, setEligible] = useState(false);
  const [hasReview, setHasReview] = useState(false);

  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  // ¿Puedo reseñar? + mi reseña actual (prellena el form).
  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const res = await fetch(`/api/products/${productId}/reviews/me`);
        if (!alive) return;
        if (res.status === 401) {
          setAuth("guest");
          return;
        }
        if (res.ok) {
          const d = (await res.json()) as {
            eligible: boolean;
            review: { rating: number; comment: string | null } | null;
          };
          setEligible(d.eligible);
          if (d.review) {
            setRating(d.review.rating);
            setComment(d.review.comment ?? "");
            setHasReview(true);
          }
        }
        setAuth("user");
      } catch {
        if (alive) setAuth("user"); // silencioso: sin form, se ven las reseñas
      }
    })();
    return () => {
      alive = false;
    };
  }, [productId]);

  const refresh = async () => {
    try {
      const res = await fetch(`/api/products/${productId}/reviews`);
      if (res.ok) {
        const d = (await res.json()) as ProductReviewsPayload;
        setSummary(d.summary);
        setReviews(d.reviews);
      }
    } catch {
      /* no-op */
    }
  };

  const submit = async () => {
    if (rating < 1 || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`/api/products/${productId}/reviews`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rating, comment: comment.trim() || undefined }),
      });
      if (!res.ok) {
        const d = (await res.json().catch(() => null)) as {
          message?: string;
        } | null;
        throw new Error(d?.message ?? "No se pudo guardar tu reseña");
      }
      setHasReview(true);
      setDone(true);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error");
    } finally {
      setSubmitting(false);
    }
  };

  const fmtDate = (iso: string) =>
    new Date(iso).toLocaleDateString("es-MX", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });

  return (
    <section className="mt-12 border-t border-muted/20 pt-8">
      <h2 className="text-2xl font-black text-dark">Reseñas</h2>

      {/* Resumen */}
      <div className="mt-3 flex items-center gap-4">
        {summary.count > 0 ? (
          <>
            <span className="text-4xl font-black text-dark leading-none">
              {summary.average.toFixed(1)}
            </span>
            <div>
              <StarRating rating={summary.average} size={18} />
              <p className="text-sm text-dark/60 mt-0.5">
                {summary.count}{" "}
                {summary.count === 1 ? "reseña" : "reseñas"}
              </p>
            </div>
          </>
        ) : (
          <p className="text-sm text-dark/60">
            Aún no hay reseñas. ¡Sé el primero en calificarlo!
          </p>
        )}
      </div>

      {/* Escribir reseña */}
      <div className="mt-6">
        {auth === "loading" && (
          <Loader2 size={18} className="animate-spin text-dark/30" />
        )}

        {auth === "guest" && (
          <div className="bg-shell rounded-xl px-4 py-3 border border-muted/20 text-sm text-dark/70">
            <a
              href={`${LANDING_URL}/login`}
              className="font-semibold text-primary hover:underline"
            >
              Inicia sesión
            </a>{" "}
            y compra este producto para dejar tu reseña.
          </div>
        )}

        {auth === "user" && !eligible && (
          <div className="bg-shell rounded-xl px-4 py-3 border border-muted/20 text-sm text-dark/60">
            Las reseñas están disponibles para quienes compraron y recibieron
            este producto.
          </div>
        )}

        {auth === "user" && eligible && (
          <div className="bg-white rounded-2xl border border-muted/20 p-4 sm:p-5">
            <p className="text-sm font-bold text-dark">
              {hasReview ? "Actualiza tu reseña" : "Escribe tu reseña"}
            </p>

            {/* Estrellas de entrada */}
            <div className="mt-3 flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((i) => {
                const active = (hover || rating) >= i;
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setRating(i)}
                    onMouseEnter={() => setHover(i)}
                    onMouseLeave={() => setHover(0)}
                    aria-label={`${i} estrella${i > 1 ? "s" : ""}`}
                    className="p-0.5"
                  >
                    <Star
                      size={28}
                      className={
                        active ? "text-yolk fill-yolk" : "text-yolk/30"
                      }
                    />
                  </button>
                );
              })}
            </div>

            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              maxLength={1000}
              placeholder="Cuéntanos qué te pareció (opcional)…"
              className="mt-3 w-full bg-shell rounded-xl px-3 py-2 text-sm text-dark placeholder:text-dark/40 focus:outline-none focus:ring-2 focus:ring-primary resize-none"
            />

            {error && (
              <p className="mt-2 text-xs font-semibold text-alert">{error}</p>
            )}

            <div className="mt-3 flex items-center gap-3">
              <button
                type="button"
                onClick={submit}
                disabled={rating < 1 || submitting}
                className="inline-flex items-center gap-2 bg-primary hover:bg-primary-dark text-white font-bold px-5 py-2.5 rounded-xl transition-colors disabled:opacity-50"
              >
                {submitting && <Loader2 size={16} className="animate-spin" />}
                {hasReview ? "Actualizar reseña" : "Publicar reseña"}
              </button>
              {done && (
                <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-success">
                  <CheckCircle2 size={16} />
                  ¡Gracias por tu reseña!
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Lista de reseñas */}
      {reviews.length > 0 && (
        <ul className="mt-8 space-y-5">
          {reviews.map((r) => (
            <li key={r.id} className="border-b border-muted/15 pb-5 last:border-0">
              <div className="flex items-center gap-2 flex-wrap">
                <StarRating rating={r.rating} size={15} />
                <span className="text-sm font-bold text-dark">
                  {r.authorName}
                </span>
                {r.verified && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-success bg-success/10 px-2 py-0.5 rounded-full">
                    <ShieldCheck size={11} />
                    Compra verificada
                  </span>
                )}
                <span className="text-xs text-dark/40 ml-auto">
                  {fmtDate(r.createdAt)}
                </span>
              </div>
              {r.comment && (
                <p className="mt-2 text-sm text-dark/75 leading-relaxed whitespace-pre-line">
                  {r.comment}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
